import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import _ from "lodash";
import moment from "moment";

//Styles
import "./styles.scss";

import SidePanel, {
  SidePanelBody,
  SidePanelHeader,
} from "Components/SidePanel";
import { Button } from "Components/uiControls";
import { Grid } from "@mui/material";
import {
  TimeFrame,
  useTimeFrameComponent,
} from "@gpstrackit/gtc-react-controls";
import SeverityOptions from "Modules/alerts/common/components/SeverityOptions";
import {
  BEHAVIOR_DATE_TODAY,
  formatEntities,
  getOptionsSelectorDate,
} from "../../redux/helpers";
import { openModalBehaviorAdvanceFilter } from "./redux/slice";
import { useNewEntityTag } from "Core/hooks";
import TagsManage from "Modules/.common/components/TagsManage";
import EntityUnitDriverSelector from "Modules/.common/components/EntityUnitDriverSelector";

const AdvanceFilterReport = (props) => {
  const { preferencesRef } = props;
  const { open, hasFilters, filter } = useSelector(
    (state) => state.reportBehaviorAdvanceFilterRedux
  );
  const dispatch = useDispatch();
  const { messages } = useIntl();

  const optionsSelectorDate = useMemo(
    () => getOptionsSelectorDate(messages),
    [messages]
  );
  const user = useSelector((state) => state.securityRedux.user);
  const [activeCalendar, setActiveCalendar] = useState(false);
  const [error, setError] = useState("");

  const timeFrame = useTimeFrameComponent(
    optionsSelectorDate,
    BEHAVIOR_DATE_TODAY
  );

  const entityTags = useNewEntityTag(
    ["Unit", "Driver"],
    messages["reportsMainRT_viewReportBy"],
    "calc(100vh - 320px)",
    messages["reportsMainRT_runReportFor"],
    filter?.entities.length
      ? filter?.entities[0].name === "Tag" || filter?.option === "tags"
        ? "tags"
        : "entities"
      : "all",
    filter?.entity ? filter?.entity : "Unit"
  );

  const changePreferences = useCallback((newValues) => {
    const hasFilters =
      (newValues?.dateOption?.id && newValues?.dateOption?.id !== "today") ||
      newValues?.entities?.length > 0 ||
      (newValues?.risk != null && newValues?.risk !== "All") ||
      (newValues.entity != null && newValues.entity !== "Unit");
    dispatch(
      openModalBehaviorAdvanceFilter({
        hasFilters: hasFilters,
        filter: newValues,
      })
    );

    const oldPreferences =
      preferencesRef?.current?.getPreferences("User", "behaviorView") || {};
    if (!_.isEqual(newValues, oldPreferences)) {
      const currentPreferences = { ...oldPreferences, ...newValues };
      preferencesRef.current.savePreference(
        "User",
        "behaviorView",
        currentPreferences
      );
    }
  }, []);

  useEffect(() => {
    if (open) {
        setTimeout(() => {
            entityTags.onChange(filter?.entities || []);
            timeFrame.defaultvalue(filter?.dateOption?.id || BEHAVIOR_DATE_TODAY);
            timeFrame.onChangeDateRange(
                moment(filter?.dateRange?.start) ?? moment().startOf("d"),
                moment(filter?.dateRange?.end) ?? moment().endOf("d")
            );
        }, 100);
    }
  }, [open]);

  const clearFilters = useCallback(() => {
    const selectedTime = optionsSelectorDate.find(
      (range) => range.id === optionsSelectorDate[0].id
    );
    timeFrame.reset();
    entityTags.onChange([]);
    entityTags.onChangeEntity({ id: "Unit" });
    entityTags.onOptionChange({ id: "all" });
    changePreferences({
      entity: "Unit",
      entities: [],
      option: "all",
      risk: null,
      dateOption: optionsSelectorDate[0],
      dateRange: selectedTime.dateRange,
    });
  }, []);

  function onChangueItems(items, entity) {
    entityTags.onChange(items);
    changePreferences({
      ...filter,
      entity: entity ? entity?.id : filter.entity,
      entities: formatEntities(items),
    });
  }

  return (
    <SidePanel
      style={{
        zIndex: 2,
        width: 400,
        transform: "translateX(400px)",
        height: "calc(100% - 50px)",
      }}
      contentStyle={{
        height: "calc(100% - 50px)",
      }}
      classSidePanel={hasFilters ? "side-panel-icon-filter" : "side-panel-icon"}
      isOpen={open}
      toggle={() => {
        dispatch(openModalBehaviorAdvanceFilter({ open: !open }));
      }}
    >
      <SidePanelHeader>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Grid item>
            <h2 style={{ margin: 0 }}>{messages["behavior_filterBy"]}</h2>
            <span className="btn-link" onClick={() => clearFilters()}>
              {" "}
              {messages["behavior_clearAll"]}{" "}
            </span>
          </Grid>
          <Grid item className="text-right">
            <Button
              type="icon"
              icon="delete"
              tooltip={messages["behavior_clearFilters"]}
              placement="left"
              onClick={() => clearFilters()}
            />
          </Grid>
        </Grid>
      </SidePanelHeader>

      <SidePanelBody
        style={{
          height: "90%",
          overFlowY: "auto",
        }}
      >
        <div className="advanced-filters_modal">
          {open && (
            <div style={{ marginTop: 20 }}>
                <EntityUnitDriverSelector
                    {...entityTags}
                    module={'report-module'}
                    onChangeEntity={(newEntity)=> {
                        changePreferences({
                            ...filter,
                            entity: newEntity.id
                        })
                        entityTags.onOptionChange({ id: 'all' })
                        entityTags.onChangeEntity(newEntity)
                    }}
                    onOptionChange={(newOption) => {
                        entityTags.onOptionChange(newOption)
                        changePreferences({
                            ...filter,
                            option: newOption.id
                        })
                    }}
                    onChange={onChangueItems}
                    entityLabelClass='report-setup-option-label'
                    entityOptionLabelClass='report-setup-type-title'
                    entityTagPickerContainerClass='report-setup-container-tag-picker'
                />
            </div>
          )}
          <div style={{ marginTop: 20 }}>
            <div className="time-frame-container">
              <TimeFrame
                {...timeFrame}
                label={messages["behavior_timeFrame"]}
                placeholder={messages["behavior_timeFrame"]}
                maxDate={moment()}
                filterSwitch={false}
                customRangeDate={90}
                withTime={true}
                openDateRangePickerModalAutomatically={activeCalendar}
                dateFormat={user?.dateformat || "MM/DD/YYYY hh:mm A"}
                onChangeSelect={(option) => {
                  if (option) {
                    const selectedTime = optionsSelectorDate.find(
                      (range) => range.id === option.id
                    );
                    timeFrame.onChangeSelect(option);
                    setActiveCalendar(option.id === "custom");

                    changePreferences({
                      ...filter,
                      dateOption: option,
                      dateRange: selectedTime.dateRange,
                    });
                  }
                }}
                onChangeDateRange={(start, end) => {
                  const startM = moment(start).startOf("day");
                  const endM = moment(end).endOf("day");
                  if (endM.diff(startM, "days") > 90) {
                    setError(messages["behavior_errorDateRange"]);
                  } else {
                    timeFrame.onChangeDateRange(startM, endM);
                    changePreferences({
                      ...filter,
                      entity: !filter?.entity ? "Unit" : filter.entity,
                      dateOption: timeFrame.option,
                      dateRange: { start: startM.toDate(), end: endM.toDate() },
                    });
                    setError("");
                  }
                }}
              />
            </div>
            <div style={{ width: "100%" }}>
              {error && (
                <small className="select-error text-danger">{error}</small>
              )}
            </div>
          </div>

          <div style={{ marginTop: 120, textAlign: "center" }}>
            <SeverityOptions
              customStyle={{ justifyContent: "center", padding: "10px 0px" }}
              label={messages["behavior_riskFactors"]}
              onChange={(newSeverity) => {
                let severity = newSeverity;
                if (newSeverity === filter?.risk) {
                  severity = null;
                }
                changePreferences({ ...filter, risk: severity });
              }}
              selectedButtons={[{ id: filter?.risk }]}
              messages={messages}
            />
          </div>
        </div>
      </SidePanelBody>
    </SidePanel>
  );
};

export default AdvanceFilterReport;
